import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import {toast} from "react-toastify";
import {
	UploadSimple,
	FileCsv,
	Check,
	Warning,
	Users,
	ArrowRight,
	ArrowLeft,
	X,
} from "@phosphor-icons/react";
import {getBaseUrlNodeServer} from "../../config";

const FILE_SIZE_LIMIT_MB = 4.0;
const PREVIEW_ROWS = 10;
const BATCH_SIZE = 10;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FIELDS = [
	{key: "Email", label: "Email", required: true},
	{key: "First Name", label: "First Name"},
	{key: "Last Name", label: "Last Name"},
	{key: "Phone", label: "Phone"},
	{key: "Company", label: "Company"},
	{key: "Position", label: "Position"},
	{key: "Notes", label: "Notes"},
];

const SKIP = "__skip__";

function autoDetectField(header) {
	const h = (header || "").toLowerCase().trim();
	if (!h) return SKIP;
	if (h.includes("email") || h.includes("e-mail") || h === "mail") return "Email";
	if (h.includes("first") && h.includes("name")) return "First Name";
	if (h === "first" || h === "fname" || h === "firstname" || h === "given") return "First Name";
	if (h.includes("last") && h.includes("name")) return "Last Name";
	if (h === "last" || h === "lname" || h === "lastname" || h === "surname" || h === "family") return "Last Name";
	if (h.includes("phone") || h.includes("mobile") || h.includes("cell") || h === "tel") return "Phone";
	if (h.includes("company") || h.includes("organization") || h.includes("organisation") || h === "org") return "Company";
	if (h.includes("position") || h.includes("title") || h.includes("role") || h.includes("job")) return "Position";
	if (h.includes("note") || h.includes("comment") || h.includes("description")) return "Notes";
	if (h === "name" || h === "full name" || h === "fullname") return "First Name";
	return SKIP;
}

// Minimal CSV parser handling quoted fields, commas in quotes, and escaped quotes ("")
function parseCsv(text) {
	const rows = [];
	let row = [];
	let cur = "";
	let inQuotes = false;
	const src = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
	for (let i = 0; i < src.length; i++) {
		const ch = src[i];
		if (inQuotes) {
			if (ch === '"' && src[i + 1] === '"') {
				cur += '"';
				i++;
			} else if (ch === '"') {
				inQuotes = false;
			} else {
				cur += ch;
			}
		} else {
			if (ch === '"') {
				inQuotes = true;
			} else if (ch === ",") {
				row.push(cur);
				cur = "";
			} else if (ch === "\n") {
				row.push(cur);
				rows.push(row);
				row = [];
				cur = "";
			} else {
				cur += ch;
			}
		}
	}
	// Flush last
	if (cur.length > 0 || row.length > 0) {
		row.push(cur);
		rows.push(row);
	}
	// Drop trailing empty row(s)
	while (rows.length && rows[rows.length - 1].every((c) => (c || "").trim() === "")) {
		rows.pop();
	}
	return rows;
}

const Stepper = ({step, goTo}) => {
	const steps = [
		{n: 1, label: "Drop"},
		{n: 2, label: "Mapping"},
		{n: 3, label: "Preview"},
		{n: 4, label: "Import"},
	];
	return (
		<div className="flex items-center justify-center gap-2 mb-8 flex-wrap">
			{steps.map((s, idx) => {
				const active = step === s.n;
				const completed = step > s.n;
				const clickable = s.n < step;
				return (
					<React.Fragment key={s.n}>
						<button
							type="button"
							disabled={!clickable}
							onClick={() => clickable && goTo(s.n)}
							className={`flex items-center gap-2 ${clickable ? "cursor-pointer" : "cursor-default"}`}
						>
							<span
								className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition ${
									active
										? "bg-emerald-500 text-white"
										: completed
											? "bg-emerald-100 text-emerald-600"
											: "bg-slate-200 text-slate-500"
								}`}
							>
								{completed ? <Check size={16} weight="bold"/> : s.n}
							</span>
							<span
								className={`text-sm font-medium ${
									active ? "text-slate-900" : completed ? "text-emerald-600" : "text-slate-500"
								}`}
							>
								{s.label}
							</span>
						</button>
						{idx < steps.length - 1 && (
							<div className="w-8 h-px bg-slate-200"/>
						)}
					</React.Fragment>
				);
			})}
		</div>
	);
};

const UploadCsv = ({userContacts}) => {
	const verifiedemail = sessionStorage.getItem("verifiedemail");
	const [step, setStep] = useState(1);
	const [file, setFile] = useState(null);
	const [rawRows, setRawRows] = useState([]); // includes header
	const [headers, setHeaders] = useState([]);
	const [mapping, setMapping] = useState({}); // headerIndex -> field key or SKIP
	const [parseError, setParseError] = useState(null);
	const [dragOver, setDragOver] = useState(false);
	const [skipExisting, setSkipExisting] = useState(true);
	const [existingEmails, setExistingEmails] = useState(new Set());
	const [existingLoading, setExistingLoading] = useState(false);
	const [importing, setImporting] = useState(false);
	const [imported, setImported] = useState(0);
	const [failed, setFailed] = useState(0);
	const [importDone, setImportDone] = useState(false);
	const fileInputRef = useRef(null);

	// Pre-seed existing emails from prop if available
	useEffect(() => {
		if (Array.isArray(userContacts) && userContacts.length > 0) {
			const set = new Set();
			userContacts.forEach((c) => {
				const e = (c && (c.Email || c.email))?.toString().trim().toLowerCase();
				if (e) set.add(e);
			});
			setExistingEmails(set);
		}
	}, [userContacts]);

	const dataRows = useMemo(() => rawRows.slice(1), [rawRows]);

	const sampleByHeader = useMemo(() => {
		const out = {};
		headers.forEach((_, i) => {
			out[i] = dataRows.slice(0, 3).map((r) => (r[i] ?? "").toString().trim()).filter(Boolean);
		});
		return out;
	}, [headers, dataRows]);

	const emailColIndex = useMemo(() => {
		const entry = Object.entries(mapping).find(([, v]) => v === "Email");
		return entry ? Number(entry[0]) : -1;
	}, [mapping]);

	const mappedRows = useMemo(() => {
		// Build {field: value} for each data row according to mapping
		return dataRows.map((row) => {
			const out = {};
			Object.entries(mapping).forEach(([colIdx, fieldKey]) => {
				if (fieldKey === SKIP) return;
				const val = row[Number(colIdx)];
				out[fieldKey] = (val ?? "").toString().trim();
			});
			return out;
		});
	}, [dataRows, mapping]);

	const validation = useMemo(() => {
		const seen = new Set();
		const items = mappedRows.map((r) => {
			const email = (r.Email || "").toLowerCase();
			let status = "ok";
			let reason = "";
			if (!email || !EMAIL_REGEX.test(email)) {
				status = "invalid";
				reason = "Invalid email";
			} else if (seen.has(email)) {
				status = "duplicate";
				reason = "Duplicate in file";
			} else if (skipExisting && existingEmails.has(email)) {
				status = "exists";
				reason = "Already in contacts";
			} else {
				seen.add(email);
			}
			return {row: r, status, reason};
		});
		const ready = items.filter((i) => i.status === "ok").length;
		const skipped = items.length - ready;
		return {items, ready, skipped};
	}, [mappedRows, skipExisting, existingEmails]);

	const resetAll = () => {
		setStep(1);
		setFile(null);
		setRawRows([]);
		setHeaders([]);
		setMapping({});
		setParseError(null);
		setImporting(false);
		setImported(0);
		setFailed(0);
		setImportDone(false);
	};

	const handleFile = useCallback((f) => {
		setParseError(null);
		if (!f) return;
		const name = f.name || "";
		if (!name.toLowerCase().endsWith(".csv")) {
			setParseError("Please upload a .csv file.");
			return;
		}
		if (f.size / (1024 * 1024) > FILE_SIZE_LIMIT_MB) {
			setParseError(`File is larger than ${FILE_SIZE_LIMIT_MB} MB.`);
			return;
		}
		const reader = new FileReader();
		reader.onerror = () => setParseError("Could not read the file.");
		reader.onload = (e) => {
			try {
				const text = e.target.result;
				const rows = parseCsv(text);
				if (!rows.length || !rows[0].length) {
					setParseError("CSV is empty.");
					return;
				}
				const hdr = rows[0].map((h) => (h ?? "").toString().trim());
				const initialMapping = {};
				const claimed = new Set();
				hdr.forEach((h, i) => {
					const guess = autoDetectField(h);
					if (guess !== SKIP && !claimed.has(guess)) {
						initialMapping[i] = guess;
						claimed.add(guess);
					} else {
						initialMapping[i] = SKIP;
					}
				});
				setFile(f);
				setRawRows(rows);
				setHeaders(hdr);
				setMapping(initialMapping);
				setStep(2);
			} catch (err) {
				setParseError("Could not parse the CSV file.");
			}
		};
		reader.readAsText(f);
	}, []);

	const onDrop = (e) => {
		e.preventDefault();
		setDragOver(false);
		const f = e.dataTransfer?.files?.[0];
		if (f) handleFile(f);
	};

	const proceedToPreview = async () => {
		if (emailColIndex < 0) {
			toast.error("Please map a column to Email before continuing.");
			return;
		}
		// Fetch existing contacts if not already loaded and toggle is on
		if (skipExisting && existingEmails.size === 0) {
			setExistingLoading(true);
			try {
				axios.defaults.baseURL = getBaseUrlNodeServer();
				const resp = await axios.post("getusercontacts", {verifiedemail});
				const set = new Set();
				(resp.data || []).forEach((c) => {
					const e = (c && (c.Email || c.email))?.toString().trim().toLowerCase();
					if (e) set.add(e);
				});
				setExistingEmails(set);
			} catch (err) {
				console.error("Could not fetch existing contacts for dedupe", err);
			} finally {
				setExistingLoading(false);
			}
		}
		setStep(3);
	};

	const startImport = async () => {
		setStep(4);
		setImporting(true);
		setImported(0);
		setFailed(0);
		setImportDone(false);

		const toImport = validation.items.filter((i) => i.status === "ok").map((i) => i.row);
		const fields = Array.from(new Set(toImport.flatMap((r) => Object.keys(r))));
		// Ensure Email is first column for consistency
		const header = ["Email", ...fields.filter((f) => f !== "Email")];

		axios.defaults.baseURL = getBaseUrlNodeServer();

		let okCount = 0;
		let failCount = 0;
		for (let i = 0; i < toImport.length; i += BATCH_SIZE) {
			const batch = toImport.slice(i, i + BATCH_SIZE);
			const results = await Promise.allSettled(
				batch.map((rec) => {
					const data = header.map((h) => rec[h] ?? "");
					// `header` MUST be a flat array. Wrapping it in another
					// array (the previous bug) made the backend store contacts
					// under a stringified-array key and corrupted every import.
					return axios.post("saveusercontact", {
						data,
						header,
						verifiedemail,
					});
				})
			);
			let batchOk = 0;
			let batchFail = 0;
			for (const r of results) {
				if (r.status === "fulfilled") batchOk++;
				else batchFail++;
			}
			okCount += batchOk;
			failCount += batchFail;
			setImported(okCount);
			setFailed(failCount);
		}

		setImporting(false);
		setImportDone(true);
		if (okCount > 0) {
			toast.success(`Imported ${okCount} contact${okCount === 1 ? "" : "s"}`);
		}
		if (failCount > 0) {
			toast.error(`${failCount} contact${failCount === 1 ? "" : "s"} failed to import`);
		}
	};

	const totalToImport = validation.ready;
	const progressPct = totalToImport > 0 ? Math.min(100, Math.round(((imported + failed) / totalToImport) * 100)) : (importDone ? 100 : 0);

	const PrimaryBtn = ({children, onClick, disabled, type = "button"}) => (
		<button
			type={type}
			onClick={onClick}
			disabled={disabled}
			className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-full px-6 py-2.5 font-semibold shadow-sm inline-flex items-center gap-2 transition"
		>
			{children}
		</button>
	);
	const SecondaryBtn = ({children, onClick, disabled}) => (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-full px-6 py-2.5 font-medium inline-flex items-center gap-2 transition disabled:opacity-50"
		>
			{children}
		</button>
	);

	return (
		<div className="w-full py-6">
			<Stepper step={step} goTo={setStep}/>

			{step === 1 && (
				<div className="rounded-2xl shadow-sm border border-slate-100 p-6 bg-white">
					<div className="mb-4">
						<h3 className="text-lg font-semibold text-slate-900">Upload your contacts</h3>
						<p className="text-sm text-slate-500 mt-1">Drop a CSV file with one row per contact. Max {FILE_SIZE_LIMIT_MB} MB.</p>
					</div>
					<label
						onDragOver={(e) => {
							e.preventDefault();
							setDragOver(true);
						}}
						onDragLeave={() => setDragOver(false)}
						onDrop={onDrop}
						className={`block border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition ${
							dragOver
								? "border-emerald-500 bg-emerald-50"
								: "border-emerald-300 bg-emerald-50/40 hover:bg-emerald-50"
						}`}
					>
						<div className="flex flex-col items-center gap-3">
							<div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
								<UploadSimple size={28} weight="bold"/>
							</div>
							<div className="text-slate-900 font-semibold">
								Drag & drop your CSV here
							</div>
							<div className="text-sm text-slate-500">
								or <span className="text-emerald-600 font-medium">click to browse</span>
							</div>
							<div className="text-xs text-slate-400 mt-1">.csv only · up to {FILE_SIZE_LIMIT_MB} MB</div>
						</div>
						<input
							ref={fileInputRef}
							type="file"
							accept=".csv,text/csv"
							className="hidden"
							onChange={(e) => handleFile(e.target.files?.[0])}
						/>
					</label>

					{file && !parseError && (
						<div className="mt-4 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
							<div className="flex items-center gap-3">
								<FileCsv size={24} className="text-emerald-600"/>
								<div>
									<div className="text-sm font-medium text-slate-900">{file.name}</div>
									<div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</div>
								</div>
							</div>
							<button
								type="button"
								onClick={resetAll}
								className="text-slate-400 hover:text-slate-600"
							>
								<X size={18}/>
							</button>
						</div>
					)}
					{parseError && (
						<div className="mt-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 text-sm">
							<Warning size={18} weight="fill"/> {parseError}
						</div>
					)}
				</div>
			)}

			{step === 2 && (
				<div className="rounded-2xl shadow-sm border border-slate-100 p-6 bg-white">
					<div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
						<div>
							<h3 className="text-lg font-semibold text-slate-900">Map your columns</h3>
							<p className="text-sm text-slate-500 mt-1">
								Match each CSV column to a contact field. Email is required.
							</p>
						</div>
						<div className="text-xs text-slate-500">
							{file?.name} · {dataRows.length} row{dataRows.length === 1 ? "" : "s"}
						</div>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						{headers.map((h, i) => (
							<div
								key={i}
								className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 flex items-start justify-between gap-4"
							>
								<div className="min-w-0">
									<div className="text-sm font-semibold text-slate-900 truncate">{h || `Column ${i + 1}`}</div>
									<div className="mt-1 text-xs text-slate-500 space-y-0.5">
										{(sampleByHeader[i] || []).slice(0, 3).map((s, k) => (
											<div key={k} className="truncate">{s}</div>
										))}
										{(!sampleByHeader[i] || sampleByHeader[i].length === 0) && (
											<div className="text-slate-400 italic">no sample data</div>
										)}
									</div>
								</div>
								<div className="shrink-0">
									<select
										value={mapping[i] ?? SKIP}
										onChange={(e) => setMapping((m) => ({...m, [i]: e.target.value}))}
										className="border border-slate-200 rounded-xl px-3 py-2 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none text-sm bg-white"
									>
										<option value={SKIP}>(skip column)</option>
										{FIELDS.map((f) => (
											<option key={f.key} value={f.key}>{f.label}</option>
										))}
									</select>
								</div>
							</div>
						))}
					</div>
					<div className="mt-6 flex items-center justify-between">
						<SecondaryBtn onClick={() => setStep(1)}>
							<ArrowLeft size={16} weight="bold"/> Back
						</SecondaryBtn>
						<PrimaryBtn onClick={proceedToPreview} disabled={emailColIndex < 0 || existingLoading}>
							{existingLoading ? "Loading…" : "Preview"} <ArrowRight size={16} weight="bold"/>
						</PrimaryBtn>
					</div>
					{emailColIndex < 0 && (
						<div className="mt-3 flex items-center gap-2 text-amber-700 text-sm">
							<Warning size={16} weight="fill"/> Map one column to <span className="font-semibold">Email</span> to continue.
						</div>
					)}
				</div>
			)}

			{step === 3 && (
				<div className="rounded-2xl shadow-sm border border-slate-100 p-6 bg-white">
					<div className="mb-4 flex items-start justify-between gap-4 flex-wrap">
						<div>
							<h3 className="text-lg font-semibold text-slate-900">Preview</h3>
							<p className="text-sm text-slate-500 mt-1">
								<span className="font-semibold text-emerald-600">{validation.ready}</span> rows ready to import,{" "}
								<span className="font-semibold text-amber-600">{validation.skipped}</span> skipped (invalid email or duplicate)
							</p>
						</div>
						<label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
							<input
								type="checkbox"
								checked={skipExisting}
								onChange={(e) => setSkipExisting(e.target.checked)}
								className="rounded border-slate-300 text-emerald-500 focus:ring-emerald-200"
							/>
							Skip duplicates from existing contacts
						</label>
					</div>

					<div className="overflow-x-auto rounded-xl border border-slate-100">
						<table className="min-w-full text-sm">
							<thead>
							<tr className="bg-slate-50 text-slate-600">
								<th className="px-3 py-2 text-left font-medium">#</th>
								{FIELDS.filter((f) => Object.values(mapping).includes(f.key)).map((f) => (
									<th key={f.key} className="px-3 py-2 text-left font-medium">{f.label}</th>
								))}
								<th className="px-3 py-2 text-left font-medium">Status</th>
							</tr>
							</thead>
							<tbody>
							{validation.items.slice(0, PREVIEW_ROWS).map((item, idx) => {
								const bad = item.status !== "ok";
								return (
									<tr
										key={idx}
										className={bad ? "bg-amber-50/70 border-t border-amber-100" : "border-t border-slate-100"}
									>
										<td className="px-3 py-2 text-slate-500">{idx + 1}</td>
										{FIELDS.filter((f) => Object.values(mapping).includes(f.key)).map((f) => (
											<td key={f.key} className="px-3 py-2 text-slate-800 truncate max-w-[220px]">
												{item.row[f.key] || <span className="text-slate-300">—</span>}
											</td>
										))}
										<td className="px-3 py-2">
											{bad ? (
												<span className="inline-flex items-center gap-1 text-amber-700 text-xs font-medium">
													<Warning size={14} weight="fill"/> {item.reason}
												</span>
											) : (
												<span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
													<Check size={14} weight="bold"/> Ready
												</span>
											)}
										</td>
									</tr>
								);
							})}
							{validation.items.length === 0 && (
								<tr>
									<td colSpan={99} className="px-3 py-6 text-center text-slate-500">No rows in this CSV.</td>
								</tr>
							)}
							</tbody>
						</table>
					</div>
					{validation.items.length > PREVIEW_ROWS && (
						<div className="mt-2 text-xs text-slate-500">
							Showing first {PREVIEW_ROWS} of {validation.items.length} rows.
						</div>
					)}

					<div className="mt-6 flex items-center justify-between">
						<SecondaryBtn onClick={() => setStep(2)}>
							<ArrowLeft size={16} weight="bold"/> Back
						</SecondaryBtn>
						<PrimaryBtn onClick={startImport} disabled={validation.ready === 0}>
							Import {validation.ready} contact{validation.ready === 1 ? "" : "s"}
							<ArrowRight size={16} weight="bold"/>
						</PrimaryBtn>
					</div>
				</div>
			)}

			{step === 4 && (
				<div className="rounded-2xl shadow-sm border border-slate-100 p-6 bg-white">
					<div className="mb-4">
						<h3 className="text-lg font-semibold text-slate-900">
							{importDone ? "Import complete" : "Importing contacts"}
						</h3>
						<p className="text-sm text-slate-500 mt-1">
							{importDone
								? `Done — imported ${imported} of ${totalToImport}.${failed > 0 ? ` ${failed} failed.` : ""}`
								: `Imported ${imported} of ${totalToImport}${failed > 0 ? ` · ${failed} failed` : ""}`}
						</p>
					</div>

					<div className="h-2 rounded-full bg-slate-100 overflow-hidden">
						<div
							className="h-2 bg-emerald-500 transition-all"
							style={{width: `${progressPct}%`}}
						/>
					</div>
					<div className="mt-2 text-xs text-slate-500 text-right">{progressPct}%</div>

					<div className="mt-6 flex items-center justify-between flex-wrap gap-3">
						<SecondaryBtn onClick={resetAll} disabled={importing}>
							Upload another file
						</SecondaryBtn>
						{importDone && (
							<PrimaryBtn
								onClick={() => {
									window.location.href = "/contacts";
								}}
							>
								<Users size={16} weight="bold"/> View contacts
							</PrimaryBtn>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default UploadCsv;
